import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Col, Form, Input, Row, Select } from "antd";
import { connect } from "dva";
import { debounce, set } from "lodash";
import { useStates } from "@/utils/address";
import useGooglePlacesAPI from "@/hooks/useGooglePlacesAPI";
import NotFoundContent from "../NotFoundContent";
import ZipCodeInput from "../ZipCodeInput";

const { Option } = Select;

let autoCompleteSessionToken = null;

const DefaultFormItemName = {
  addressLine1: "addressLine1",
  addressLine2: "addressLine2",
  stateId: "stateId",
  stateName: "stateName",
  stateShortName: "stateShortName",
  zipCode: "zipCode",
  city: "city",
};

const AddressInput = ({
  addressLine1FormItemProps = {},
  addressLine2FormItemProps = {},
  stateIdFormItemProps = {},
  stateNameFormItemProps = {},
  stateShortNameFormItemProps = {},
  cityFormItemProps = {},
  zipCodeFormItemProps = {},

  addressLine1InputProps = {},
  addressLine2InputProps = {},
  stateInputProps = {},
  cityInputProps = {},
  zipCodeInputProps = {},

  formReference,
  disabled = false,
  locationType,

  states: propStates = null,
  loadingStates: propLoadingStates = null,

  onSearch = undefined,
  onSelect = undefined,
  onFocus = undefined,
  children = undefined,
  configLang = {},
  colForm = false,
  customBorder = false,
  doubleLineForm = false,
}) => {
  const zipCodeInputRef = useRef();

  const [addresssesOptions, setAddressesOptions] = useState([]);

  const [placesService, autocompleteService] = useGooglePlacesAPI();

  const [wasStateAutofilled, setWasStateAutofilled] = useState(false);

  const { loading: hookLoadingStates, states: hookStates } = useStates(
    disabled || (propStates !== null && propLoadingStates !== null),
  );

  const { loadingStates, states } = useMemo(() => {
    if (Array.isArray(propStates) && propLoadingStates !== null) {
      return {
        loadingStates: propLoadingStates,
        states: propStates,
      };
    }

    return {
      loadingStates: hookLoadingStates,
      states: hookStates,
    };
  }, [propLoadingStates, propStates, hookLoadingStates, hookStates]);

  useEffect(
    () => () => {
      autoCompleteSessionToken = null;
    },
    [],
  );

  useEffect(() => {
    if (states && !loadingStates) {
      const stateObj = states.find(
        state =>
          state.name === formReference?.current?.getFieldValue(stateNameFormItemProps.name) ||
          state.id === formReference?.current?.getFieldValue(stateIdFormItemProps.name),
      );

      if (stateObj) {
        const newValues = {};
        set(newValues, stateIdFormItemProps.name, stateObj.id);
        set(newValues, stateShortNameFormItemProps.name, stateObj.shortName);
        set(newValues, stateNameFormItemProps.name, stateObj.name);
        formReference?.current?.setFieldsValue(newValues);
      }
    }
  }, [states, loadingStates, formReference, stateNameFormItemProps, stateIdFormItemProps, stateShortNameFormItemProps]);

  const { labelLine1Component, labelLine1Text } = useMemo(() => {
    const text = addressLine1FormItemProps?.label ?? configLang.CoreService?.AddressLine1;
    return {
      labelLine1Text: text,
      labelLine1Component: (
        <>
          <span style={{ marginRight: 5 }}>{text} </span>
          <img style={{ width: "100px" }} alt="Powered by Google" src="/img/powered_by_google_on_white_hdpi.png" />
        </>
      ),
    };
  }, [configLang, addressLine1FormItemProps]);

  const fieldItemNames = useMemo(
    () => ({
      line1: addressLine1FormItemProps?.name || DefaultFormItemName.addressLine1,
      line2: addressLine2FormItemProps?.name || DefaultFormItemName.addressLine2,
      stateId: stateIdFormItemProps?.name || DefaultFormItemName.stateId,
      stateName: stateNameFormItemProps?.name || DefaultFormItemName.stateName,
      stateShortName: stateShortNameFormItemProps?.name || DefaultFormItemName.stateShortName,
      zipCode: zipCodeFormItemProps?.name || DefaultFormItemName.zipCode,
      city: cityFormItemProps?.name || DefaultFormItemName.city,
    }),
    [
      addressLine1FormItemProps?.name,
      addressLine2FormItemProps?.name,
      stateIdFormItemProps?.name,
      stateNameFormItemProps?.name,
      stateShortNameFormItemProps?.name,
      zipCodeFormItemProps?.name,
      cityFormItemProps?.name,
    ],
  );

  const fieldItemProps = useMemo(
    () => ({
      line1: {
        rules: addressLine1FormItemProps?.rules || [
          {
            required: true,
            validator: (_, value) => {
              if (!value) {
                return Promise.reject(`Please enter ${labelLine1Text}`);
              }

              return Promise.resolve();
            },
          },
        ],
        ...addressLine1FormItemProps,
        label: labelLine1Component,
      },
      line2: {
        label: addressLine2FormItemProps?.label || configLang.CoreService?.AddressLine2,
        ...addressLine2FormItemProps,
      },
      stateId: {
        label: stateIdFormItemProps?.label || configLang.CoreService?.State,
        rules: stateIdFormItemProps?.rules || [{ required: true }],
        ...stateIdFormItemProps,
      },
      stateName: {
        rules: stateNameFormItemProps?.rules || [{ required: true }],
        ...stateNameFormItemProps,
      },
      stateShortName: {
        rules: stateShortNameFormItemProps?.rules || [{ required: true }],
        ...stateShortNameFormItemProps,
      },
      zipCode: {
        label: zipCodeFormItemProps?.label || configLang.CoreService?.ZipCode,
        ...zipCodeFormItemProps,
      },
      city: {
        label: cityFormItemProps?.label || configLang.CoreService?.City,
        rules: addressLine1FormItemProps?.rules || [
          { required: true },
          {
            pattern: /^[a-zA-Z\s]*$/,
            message: "Please enter a valid city",
          },
        ],
        ...cityFormItemProps,
      },
    }),
    [
      labelLine1Text,
      labelLine1Component,
      addressLine1FormItemProps,
      addressLine2FormItemProps,
      stateIdFormItemProps,
      configLang,
      stateNameFormItemProps,
      stateShortNameFormItemProps,
      zipCodeFormItemProps,
      cityFormItemProps,
    ],
  );

  const handleUpdateStateNames = useCallback(
    stateId => {
      // "Pickup" or "Dropoff" nested level
      const hasLocationType = locationType !== null && locationType !== undefined;

      if (stateId === null) {
        // unknown state from an address, it could be from another country and
        // (we only work on the US but do not know what the user selects)
        if (hasLocationType) {
          formReference?.current?.setFieldsValue({
            [locationType]: {
              [fieldItemNames.stateName[1]]: null,
              [fieldItemNames.stateShortName[1]]: null,
            },
          });
        } else {
          const newValues = {};
          set(newValues, fieldItemNames.stateName, null);
          set(newValues, fieldItemNames.stateShortName, null);
          formReference?.current?.setFieldsValue(newValues);
        }

        return;
      }

      const { name: stateName, shortName: stateShortName } = states.find(state => state.id === stateId);

      if (hasLocationType) {
        formReference?.current?.setFieldsValue({
          [locationType]: {
            [fieldItemNames.stateName[1]]: stateName,
            [fieldItemNames.stateShortName[1]]: stateShortName,
          },
        });
      } else {
        const newValues = {};
        set(newValues, fieldItemNames.stateName, stateName);
        set(newValues, fieldItemNames.stateShortName, stateShortName);
        formReference?.current?.setFieldsValue(newValues);
      }
    },
    [fieldItemNames.stateName, fieldItemNames.stateShortName, formReference, locationType, states],
  );

  const addressOptionsSelect = useCallback(
    (value, option) => {
      const hasLocationType = locationType !== null && locationType !== undefined;

      const request = {
        placeId: option.key,
        fields: ["address_component", "formatted_address"],
      };

      placesService.getDetails(request, place => {
        const address = place.formatted_address.split(",")[0];
        const city = place.address_components.filter(data => data.types.includes("locality"))[0]?.long_name;
        const shortStateName = place.address_components.filter(data =>
          data.types.includes("administrative_area_level_1"),
        )[0]?.short_name;
        const zipCode = place.address_components.filter(data => data.types.includes("postal_code"))[0]?.long_name;

        const selectedState = states.find(state => state.shortName === shortStateName) || null;

        let newValues = {};

        // Object in case of usage in shipment form for nested Pickup/Dropoff levels
        if (hasLocationType) {
          newValues = {
            [locationType]: {
              [fieldItemNames.line1[1]]: `${address || ""}`,
              [fieldItemNames.stateId[1]]: selectedState === null ? undefined : selectedState.id,
              [fieldItemNames.stateShortName[1]]: selectedState === null ? undefined : shortStateName,
              [fieldItemNames.zipCode[1]]: `${zipCode || ""}`,
              [fieldItemNames.city[1]]: `${city || ""}`,
            },
          };
        } else {
          set(newValues, fieldItemNames.line1, `${address || ""}`);
          set(newValues, fieldItemNames.stateId, selectedState?.id);
          set(newValues, fieldItemNames.stateShortName, shortStateName);
          set(newValues, fieldItemNames.zipCode, `${zipCode || ""}`);
          set(newValues, fieldItemNames.city, `${city || ""}`);
        }

        formReference?.current?.setFieldsValue(newValues);
        handleUpdateStateNames(selectedState?.id || null);
      });
    },
    [locationType, placesService, states, formReference, fieldItemNames, handleUpdateStateNames],
  );

  const autoCompleteAddress = value => {
    autocompleteService.getPlacePredictions(
      {
        input: value,
        sessionToken: autoCompleteSessionToken,
      },
      predictions => {
        setAddressesOptions(predictions || []);
      },
    );
  };

  const onChangeAddress = debounce(autoCompleteAddress, 500);

  const checkAutoComplete = useCallback(event => {
    if (event.target.autocomplete) {
      // eslint-disable-next-line no-param-reassign
      event.target.autocomplete = "stop";
    }
  }, []);

  const addressesOptionsRender = useMemo(
    () =>
      addresssesOptions.map(address => (
        <Option key={address.place_id} value={address.description}>
          {address.description}
        </Option>
      )),
    [addresssesOptions],
  );

  const addressLine1FormItemRender = useMemo(
    () => (
      <Form.Item {...fieldItemProps.line1}>
        <AutoComplete
          // getPopupContainer prop set like this fixes visual scroll bug when menu is open
          getPopupContainer={trigger => trigger.parentElement}
          onSearch={onSearch || onChangeAddress}
          onSelect={onSelect || addressOptionsSelect}
          onFocus={onFocus || checkAutoComplete}
          disabled={disabled}
          className={customBorder ? "selector-custom-border" : ""}
          {...addressLine1InputProps}
        >
          {children || addressesOptionsRender}
        </AutoComplete>
      </Form.Item>
    ),
    [
      fieldItemProps.line1,
      disabled,
      customBorder,
      addressLine1InputProps,
      children,
      addressesOptionsRender,
      onSearch,
      onSelect,
      addressOptionsSelect,
      onFocus,
      checkAutoComplete,
      onChangeAddress,
    ],
  );

  const addressLine2FormItemRender = useMemo(
    () => (
      <Form.Item {...fieldItemProps.line2}>
        <Input disabled={disabled} {...addressLine2InputProps} />
      </Form.Item>
    ),
    [fieldItemProps.line2, disabled, addressLine2InputProps],
  );

  const onStateIdChange = useCallback(
    stateId => {
      const state = states.find(({ id }) => id === stateId);
      if (!state) {
        return;
      }

      const newValues = {};
      set(newValues, fieldItemNames.stateName, state.name);
      set(newValues, fieldItemNames.stateShortName, state.shortName);
      formReference?.current?.setFieldsValue(newValues);
      setWasStateAutofilled(false);
    },
    [states, fieldItemNames?.stateName, fieldItemNames?.stateShortName, formReference],
  );

  const handleKeyDownOnStateSelect = e => {
    // Ensure we focus the zipcode input next instead of the hidden states input
    if (e.key === "Tab") {
      e.preventDefault();
      zipCodeInputRef.current.focus();
    }
  };

  const stateIdFormItemRender = useMemo(() => {
    let className = "";

    if (customBorder) className += "selector-custom-border";
    if (wasStateAutofilled) className += " custom-autofilled-select";

    return (
      <>
        <Form.Item {...fieldItemProps.stateId}>
          <Select
            // getPopupContainer prop set like this fixes visual scroll bug when menu is open
            getPopupContainer={trigger => trigger.parentElement}
            showSearch
            placeholder={configLang.CoreService?.StatePlaceholder}
            autoComplete="dontshow"
            disabled={disabled}
            notFoundContent={NotFoundContent({
              loading: loadingStates,
            })}
            filterOption={(input, option) => option.key.toUpperCase().indexOf(input.toUpperCase()) !== -1}
            className={className}
            onChange={onStateIdChange}
            onKeyDown={handleKeyDownOnStateSelect}
            {...stateInputProps}
          >
            {states.map(({ name, shortName, id }) => (
              <Select.Option key={`${name || ""}${shortName || ""}${id || ""}`} value={id}>
                {shortName ?? name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <input
          style={{
            position: "absolute",
            opacity: 0,
            height: 0,
            pointerEvents: "none",
          }}
          autcomplete="address-level1"
          type="text"
          id="state"
          name="state"
          onChange={e => {
            // Detect Chrome. Autofill coloring and other functionalities might break in other browsers, so don't try.
            const isChrome = navigator.userAgent.indexOf("Chrome") > -1;

            if (!isChrome) return;

            const state = e.target.value;

            const matchingStateData = states.find(_state => _state.shortName === state);

            if (!matchingStateData) return;

            const stateIsAlreadySelected = formReference.current.getFieldValue(fieldItemProps.stateId.name);

            // Chrome autofill ignores fields with values already in them. Do same behavior for our manual autofill.
            if (stateIsAlreadySelected) return;

            formReference.current.setFieldsValue({
              [fieldItemProps.stateId.name]: matchingStateData.id,
            });

            onStateIdChange(matchingStateData.id);

            setWasStateAutofilled(true);
          }}
        />
      </>
    );
  }, [
    customBorder,
    wasStateAutofilled,
    fieldItemProps.stateId,
    configLang,
    disabled,
    loadingStates,
    onStateIdChange,
    stateInputProps,
    states,
    formReference,
  ]);

  const zipCodeFormItemRender = useMemo(
    () => (
      <ZipCodeInput
        inputRef={zipCodeInputRef}
        formRef={formReference}
        formItemProps={fieldItemProps.zipCode}
        disabled={disabled}
        inputProps={zipCodeInputProps}
      />
    ),
    [fieldItemProps, disabled, zipCodeInputProps, zipCodeInputRef, formReference],
  );

  const cityFormItemRender = useMemo(
    () => (
      <Form.Item {...fieldItemProps.city}>
        <Input {...cityInputProps} disabled={disabled} />
      </Form.Item>
    ),
    [fieldItemProps.city, cityInputProps, disabled],
  );

  const stateNameFormItemRender = useMemo(
    () => (
      <Form.Item {...fieldItemProps.stateName} hidden>
        <Input />
      </Form.Item>
    ),
    [fieldItemProps.stateName],
  );

  const stateShortNameFormItemRender = useMemo(
    () => (
      <Form.Item {...fieldItemProps.stateShortName} hidden>
        <Input />
      </Form.Item>
    ),
    [fieldItemProps.stateShortName],
  );

  if (colForm) {
    return (
      <>
        {addressLine1FormItemRender}
        {addressLine2FormItemRender}
        {stateIdFormItemRender}
        {stateNameFormItemRender}
        {stateShortNameFormItemRender}
        {zipCodeFormItemRender}
        {cityFormItemRender}
      </>
    );
  }

  return (
    <>
      {doubleLineForm ? (
        <Col flex={1} xs={24}>
          <Row>
            <Col flex={1}>{addressLine1FormItemRender}</Col>
          </Row>
          <Row>
            <Col flex={1}>{addressLine2FormItemRender}</Col>
          </Row>
        </Col>
      ) : (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            {addressLine1FormItemRender}
          </Col>
          <Col xs={24} lg={12}>
            {addressLine2FormItemRender}
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col xs={24} md={24} lg={8}>
          {stateIdFormItemRender}
          {stateNameFormItemRender}
          {stateShortNameFormItemRender}
        </Col>
        <Col xs={24} lg={8}>
          {zipCodeFormItemRender}
        </Col>
        <Col xs={24} lg={8}>
          {cityFormItemRender}
        </Col>
      </Row>
    </>
  );
};

AddressInput.defaultProps = {
  locationType: undefined,
  addressLine1FormItemProps: {
    name: DefaultFormItemName.addressLine1,
  },
  addressLine2FormItemProps: {
    name: DefaultFormItemName.addressLine2,
  },
  stateIdFormItemProps: {
    name: DefaultFormItemName.stateId,
  },
  stateNameFormItemProps: {
    name: DefaultFormItemName.stateName,
  },
  stateShortNameFormItemProps: {
    name: DefaultFormItemName.stateShortName,
  },
  cityFormItemProps: {
    name: DefaultFormItemName.city,
  },
  zipCodeFormItemProps: {
    name: DefaultFormItemName.zipCode,
  },
};

export default connect(({ configs }) => ({
  configLang: configs.configLang,
}))(AddressInput);
